import type { ReactNode } from "react";
import { Box, Group, Modal, Text } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { Button } from "./button";

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  confirmLabel: string;
  destructive?: boolean;
  pending?: boolean;
  /** For an action the body already explains cannot go ahead yet. */
  confirmDisabled?: boolean;
  children: ReactNode;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Confirmation modal, backed by Mantine.
 *
 * Focus trap, Escape, overlay click and `role="dialog"` come from the library
 * rather than a native `<dialog>` shim — the same behaviour, without the
 * jsdom polyfill carrying the production path (ADR-075).
 */
export function ConfirmDialog({
  open,
  title,
  confirmLabel,
  destructive = false,
  pending = false,
  confirmDisabled = false,
  children,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const { t } = useTranslation("common");

  return (
    <Modal
      opened={open}
      onClose={() => {
        if (!pending) {
          onCancel();
        }
      }}
      title={title}
      size="sm"
      // Instant open: a CSS-less test environment never finishes the enter
      // transition, so the dialog would stay `aria-hidden` and unfindable.
      transitionProps={{ duration: 0 }}
      closeOnClickOutside={!pending}
      closeOnEscape={!pending}
      withCloseButton={!pending}
    >
      <Box mb="lg">
        <Text size="sm" c="dimmed" component="div">
          {children}
        </Text>
      </Box>

      <Group justify="flex-end" gap="sm" mt="md">
        <Button variant="secondary" onClick={onCancel} disabled={pending}>
          {t("cancel")}
        </Button>
        <Button
          variant={destructive ? "danger" : "primary"}
          onClick={onConfirm}
          loading={pending}
          disabled={confirmDisabled}
        >
          {confirmLabel}
        </Button>
      </Group>
    </Modal>
  );
}
